import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgAddToCalendarService, ICalendarEvent } from '@trademe/ng-add-to-calendar';
import { forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import * as _ from 'lodash';
import * as moment from 'moment';
import { environment } from '../../../../environments/environment';
import { CompanyMasterService } from '../../../services/companymaster.service';
import { DashboardMyTaskService } from '../../../services/dashboardmytask.service';
import { GfmService } from '../../../services/gfm.service';
import { MeetingMasterService } from '../../../services/meetingMaster.service';
import { MeetingdetailsComponent } from '../meetingdetails/meetingdetails.component';

interface DirectorDocumentWidgetItem {
  label: string;
  name: string;
  fileId: string;
  fileExt: string;
  uploadDate?: string | Date;
}

interface DirectorParticipantWidgetItem {
  name: string;
  role: string;
}

@Component({
  selector: 'app-director-dashboard',
  templateUrl: './director-dashboard.component.html',
  styleUrls: ['./director-dashboard.component.css', '../alpha.css'],
})
export class DirectorDashboardComponent implements OnInit, OnDestroy {
  isLoading = true;
  isEmpty = false;
  agendaPreviewExpanded = true;
  documentsWidgetExpanded = true;
  documentsWidgetItems: DirectorDocumentWidgetItem[] = [];
  participantsAttending: DirectorParticipantWidgetItem[] = [];
  participantsNotAttending: DirectorParticipantWidgetItem[] = [];
  participantsInvited: DirectorParticipantWidgetItem[] = [];
  participantsHasAttendance = false;
  participantsAttendedCount = 0;
  participantsNotAttendedCount = 0;
  participantsWidgetTotal = 0;
  participantsModalOpen = false;
  companyData: any = null;

  nextMeeting: any = null;
  upcomingMeetings: any[] = [];
  earlierTodayMeetings: any[] = [];
  nextMeetingIsLive = false;
  nextMeetingLiveEndLabel = '';
  agendaPreviewItems: any[] = [];

  userDisplayName = '';
  daysUntilNextMeeting: number | null = null;
  nextMeetingCompanyName = '';
  nextMeetingAgendaCount = 0;
  nextMeetingDocumentCount = 0;
  nextMeetingAttendeeCount = 0;
  nextMeetingDurationLabel = '90 min';

  showAgendaView = false;

  pdfModalOpen = false;
  pdfModalTitle = '';
  pdfModalSubtitle = '';
  pdfLoading = false;
  pdfError = '';
  pdfIframeUrl: SafeResourceUrl | null = null;
  calendarRefreshToken: any = { refreshedAt: 0 };
  shareInfoWidgetActive = false;
  financialsWidgetActive = false;
  keyRatiosWidgetActive = false;
  orgChartWidgetActive = false;
  calendarUrls: {
    google: SafeUrl;
    iCalendar: SafeUrl;
    outlook: SafeUrl;
    yahoo: SafeUrl;
    microsoft: SafeUrl;
  } | null = null;

  private meetingListCache: any[] = [];
  calendarPrefetchedMeetings: any[] = [];
  calendarPrefetchedUserList: any[] = [];
  private readonly DEFAULT_LIVE_WINDOW_MIN = 60;
  private readonly LIVE_STATUS_POLL_MS = 15000;
  private meetingRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private todayDate: number;
  private isMeetingsFetch: string;
  private gemAccessURL = '';
  private complianceEngine = '';
  private complianceEngineUrl = '';
  private governanceUrl = environment.nonValidateUrl;

  constructor(
    private companyMasterService: CompanyMasterService,
    private meetingMasterService: MeetingMasterService,
    private dashboardMyTaskService: DashboardMyTaskService,
    private gfmService: GfmService,
    private modalService: NgbModal,
    private router: Router,
    private sanitizer: DomSanitizer,
    private addToCalendarService: NgAddToCalendarService
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.isEmpty = false;
    this.todayDate = new Date().setHours(0, 0, 0, 0);
    this.isMeetingsFetch = localStorage.getItem('isMeetingsFetch');
    this.setUserDisplayName();
    this.loadGemAccessConfig();
    this.companyMasterService.ensureStockSymbol().catch(() => {});

    if (this.isMeetingsFetch !== 'false') {
      this.loadMeetings();
    } else {
      this.finishLoadingEmpty();
    }

    this.meetingRefreshTimer = setInterval(() => {
      if (!this.isLoading && this.meetingListCache.length) {
        this.refreshGemLiveStatusForTodayMeetings().then(() => {
          this.assignMeetingStatusAndBuildView(false);
        });
      }
    }, this.LIVE_STATUS_POLL_MS);
  }

  ngOnDestroy() {
    if (this.meetingRefreshTimer) {
      clearInterval(this.meetingRefreshTimer);
      this.meetingRefreshTimer = null;
    }
    document.body.style.overflow = '';
    this.participantsModalOpen = false;
  }

  get greeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) {
      return 'Good morning';
    }
    if (hour < 17) {
      return 'Good afternoon';
    }
    return 'Good evening';
  }

  get nextMeetingHeadline(): string {
    if (this.isLoading) {
      return 'Loading your meetings…';
    }
    if (!this.nextMeeting) {
      return 'No upcoming meetings';
    }
    if (this.nextMeetingIsLive) {
      return 'Your meeting is live now';
    }
    if (this.daysUntilNextMeeting === 0) {
      return 'Your next meeting is today';
    }
    if (this.daysUntilNextMeeting === 1) {
      return 'Your next meeting is tomorrow';
    }
    const meetingDay = moment(this.getMeetingDateRaw(this.nextMeeting)).startOf('day');
    if (meetingDay.isValid()) {
      return `Your next meeting is on ${meetingDay.format('D MMM')}`;
    }
    return `Your next meeting is in ${this.daysUntilNextMeeting} days`;
  }

  toggleAgendaPreview() {
    this.agendaPreviewExpanded = !this.agendaPreviewExpanded;
  }

  toggleDocumentsWidget() {
    this.documentsWidgetExpanded = !this.documentsWidgetExpanded;
  }

  openParticipantsModal() {
    this.participantsModalOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeParticipantsModal() {
    this.participantsModalOpen = false;
    if (!this.pdfModalOpen) {
      document.body.style.overflow = '';
    }
  }

  openDashboardModal(modalId: string) {
    switch (modalId) {
      case 'ShareInfoModal':
        this.shareInfoWidgetActive = true;
        if (!this.companyData) {
          this.loadCompanyData();
        }
        break;
      case 'FinancialsModal':
        this.financialsWidgetActive = true;
        break;
      case 'KeyRatiosModal':
        this.keyRatiosWidgetActive = true;
        break;
      case 'orgChartModal':
        this.orgChartWidgetActive = true;
        break;
    }

    setTimeout(() => {
      try {
        const $modal = (window as any)['$'] ? (window as any)['$'](`#${modalId}`) : null;
        if ($modal && $modal.modal) {
          $modal.modal('show');
        }
      } catch (e) {
        console.error('Error opening dashboard modal', modalId, e);
      }
    }, 0);
  }

  private loadCompanyData() {
    this.companyMasterService
      .getCompanyList()
      .then((company) => {
        if (company.status === 0) {
          this.companyData = company.companyData;
          if (company.companyData?.Symbol) {
            localStorage.setItem('Symbol', String(company.companyData.Symbol).trim());
          }
        }
      })
      .catch(() => {});
  }

  openDocumentWidget(item: DirectorDocumentWidgetItem) {
    if (!item || !item.fileId) {
      return;
    }
    if (this.isPdfExt(item.fileExt)) {
      this.openDocumentPdfViewer(item);
      return;
    }
    this.meetingMasterService.getAttendanceFile(item.fileId);
  }

  /** Same Adobe ViewSDK meeting popup used for agenda PDFs / meeting Documents tab. */
  openDocumentPdfViewer(item: DirectorDocumentWidgetItem) {
    if (!this.nextMeeting || !item || !item.fileId) {
      return;
    }

    const label = (item.label || '').toLowerCase();
    let minuteType: 'DRAFT' | 'FINAL' | null = null;
    if (label.indexOf('draft') >= 0) {
      minuteType = 'DRAFT';
    } else if (label.indexOf('final') >= 0) {
      minuteType = 'FINAL';
    }

    const modalRef = this.modalService.open(MeetingdetailsComponent, {
      windowClass: 'modal-xlC',
    });
    modalRef.componentInstance.meetingObject = this.nextMeeting;
    modalRef.componentInstance.documentPdfOnlyMode = true;
    modalRef.componentInstance.autoOpenFileId = item.fileId;
    modalRef.componentInstance.autoOpenDocumentTitle = item.label || item.name || 'Document';
    modalRef.componentInstance.autoOpenDocumentMinuteType = minuteType;
    modalRef.componentInstance.autoOpenDocumentCanVote = !!minuteType;
    this.bindMeetingModalResult(modalRef);
  }

  openMeetingDetails(meeting: any) {
    const modalRef = this.modalService.open(MeetingdetailsComponent, {
      windowClass: 'modal-xlC',
    });
    modalRef.componentInstance.meetingObject = meeting;
    this.bindMeetingModalResult(modalRef);
  }

  joinMeeting(meeting: any) {
    const modalRef = this.modalService.open(MeetingdetailsComponent, {
      windowClass: 'modal-dialog-centered',
      size: 'sm',
    });
    modalRef.componentInstance.meetingObject = meeting;
    modalRef.componentInstance.actionOnlyMode = true;
    modalRef.componentInstance.autoExecuteJoin = true;
    this.bindMeetingModalResult(modalRef);
  }

  private bindMeetingModalResult(modalRef: any) {
    modalRef.result.then(
      (response) => {
        const joinInPerson = localStorage.getItem('joinInPerson');
        if (joinInPerson != 'yes') {
          this.router.navigate(['webex', response]);
        } else {
          this.router.navigate(['/attending-inperson', response]);
        }
      },
      () => {}
    ).catch(() => {});
  }

  reviewAgenda() {
    if (this.nextMeeting) {
      this.showAgendaView = true;
    }
  }

  backFromAgenda() {
    this.showAgendaView = false;
  }

  onAgendaFileURL(event: any) {
    this.openAgendaPdfViewer(null, event);
  }

  viewCalendar() {
    const el = document.querySelector('.dd-calendar');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    this.router.navigate([
      'structure',
      { outlets: { 'structure-outlet': 'dashboards/alpha' } },
    ]);
  }

  onCalendarOutput(_event: any) {
    this.refreshCalendarWidget();
  }

  private refreshCalendarWidget() {
    this.calendarPrefetchedMeetings = [];
    this.calendarPrefetchedUserList = [];
    this.calendarRefreshToken = { refreshedAt: Date.now() };
  }

  private setUserDisplayName() {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const first = user && user.FirstName ? user.FirstName : '';
      const last = user && user.LastName ? user.LastName : '';
      this.userDisplayName = `${first} ${last}`.trim() || 'Director';
    } catch (e) {
      this.userDisplayName = 'Director';
    }
  }

  isMeetingMissed(meeting: any): boolean {
    if (!meeting || this.classifyMeetingBucket(meeting) !== 'EARLIER_TODAY') {
      return false;
    }
    return !this.hasAttendanceLogged(meeting);
  }

  private hasAttendanceLogged(meeting: any): boolean {
    const attendance = meeting?.Attendance;
    if (!Array.isArray(attendance) || !attendance.length) {
      return false;
    }
    return attendance.some((record: any) => record?.isAttended === true);
  }

  private classifyMeetingBucket(
    meeting: any,
    nowMs: number = Date.now()
  ): 'LIVE' | 'UPCOMING' | 'EARLIER_TODAY' | 'PAST' {
    if (!this.isPublishedMeeting(meeting)) {
      return 'PAST';
    }

    const startMs = this.meetingStartMs(meeting);
    if (!startMs) {
      return 'PAST';
    }

    const session = this.getMeetingSessionState(meeting);
    const meetingDay = moment(this.getMeetingDateRaw(meeting)).startOf('day');
    const today = moment().startOf('day');
    const isToday = meetingDay.isSame(today, 'day');

    if (session.hasEnded) {
      return isToday ? 'EARLIER_TODAY' : (startMs > nowMs ? 'UPCOMING' : 'PAST');
    }

    if (session.hasStarted && !session.hasEnded) {
      if (isToday) {
        return 'LIVE';
      }
      return startMs > nowMs ? 'UPCOMING' : 'PAST';
    }

    if (startMs > nowMs) {
      return 'UPCOMING';
    }

    if (isToday) {
      return 'EARLIER_TODAY';
    }

    return 'PAST';
  }

  private getMeetingSessionState(meeting: any): {
    hasStarted: boolean;
    hasEnded: boolean;
  } {
    const gem = meeting?._gemLiveStatus;
    if (gem) {
      const endTime = gem.meetingEndTime;
      return {
        hasStarted: gem.hasMeetingStarted === true,
        hasEnded: endTime != null && String(endTime).trim() !== '',
      };
    }

    const attr = Array.isArray(meeting?.AttendanceAttribute)
      ? meeting.AttendanceAttribute[0]
      : meeting?.AttendanceAttribute;
    const startTime = attr?.MeetingStartTime;
    const endTime = attr?.MeetingEndTime;
    return {
      hasStarted: !!(startTime && String(startTime).trim()),
      hasEnded: !!(endTime && String(endTime).trim()),
    };
  }

  private loadGemAccessConfig(): void {
    this.governanceUrl = environment.nonValidateUrl;
    this.companyMasterService
      .getCompanyList()
      .then((companyResponse) => {
        if (companyResponse?.companyData) {
          this.complianceEngine = companyResponse.companyData.ComplianceEngine;
          this.complianceEngineUrl = companyResponse.companyData.ComplianceEngineUrl;
          const gemUrl = companyResponse.companyData.GEMAccessURL;
          if (gemUrl) {
            this.gemAccessURL = gemUrl;
          } else {
            this.gemAccessURL = 'http://' + environment.gemIp_domain + '/api/';
          }
        }
      })
      .catch(() => {});
  }

  private refreshGemLiveStatusForTodayMeetings(): Promise<void> {
    if (!environment.Service?.GemWeb || !this.gemAccessURL) {
      return Promise.resolve();
    }

    const today = moment().startOf('day');
    const todaysMeetings = this.meetingListCache.filter((meeting) => {
      if (!this.isPublishedMeeting(meeting)) {
        return false;
      }
      const meetingDay = moment(this.getMeetingDateRaw(meeting)).startOf('day');
      return meetingDay.isSame(today, 'day');
    });

    if (!todaysMeetings.length) {
      return Promise.resolve();
    }

    const tasks = todaysMeetings.map((meeting) => {
      const meetingId = meeting._id || meeting.meetingId;
      if (!meetingId) {
        return Promise.resolve();
      }
      return this.gfmService
        .isMeetingStarted(
          meetingId,
          this.complianceEngine,
          this.complianceEngineUrl,
          this.governanceUrl,
          this.gemAccessURL
        )
        .then((response) => {
          const attendance = response?.meetingAttendance;
          if (attendance) {
            meeting._gemLiveStatus = {
              hasMeetingStarted: attendance.hasMeetingStarted === true,
              meetingEndTime: attendance.meetingEndTime,
              meetingStartTime: attendance.meetingStartTime,
            };
          }
        })
        .catch(() => {});
    });

    return Promise.all(tasks).then(() => undefined);
  }

  private getMeetingDurationMinutes(meeting: any): number {
    const agendas = this.getPublishedAgendas(meeting);
    if (agendas.length) {
      return Math.max(agendas.length * 10, 30);
    }
    return this.DEFAULT_LIVE_WINDOW_MIN;
  }

  private meetingEndMs(meeting: any): number {
    const startMs = this.meetingStartMs(meeting);
    if (!startMs) {
      return 0;
    }

    const endDateRaw = meeting?.MeetingEndDate || meeting?.meetingEndDate;
    const endTimeRaw = meeting?.MeetingEndTime || meeting?.meetingEndTime;
    const startDateStr = moment(this.getMeetingDateRaw(meeting)).format('YYYY-MM-DD');
    const timeFormats = [
      'YYYY-MM-DD h:mm A',
      'YYYY-MM-DD hh:mm A',
      'YYYY-MM-DD H:mm',
      'YYYY-MM-DD HH:mm',
    ];

    if (endDateRaw) {
      const endDateStr = moment(endDateRaw).format('YYYY-MM-DD');
      if (endTimeRaw) {
        const parsedEnd = moment(`${endDateStr} ${endTimeRaw}`, timeFormats);
        if (parsedEnd.isValid()) {
          return parsedEnd.valueOf();
        }
      }
      return moment(endDateStr, 'YYYY-MM-DD').endOf('day').valueOf();
    }

    if (endTimeRaw && startDateStr) {
      const parsedEnd = moment(`${startDateStr} ${endTimeRaw}`, timeFormats);
      if (parsedEnd.isValid() && parsedEnd.valueOf() > startMs) {
        return parsedEnd.valueOf();
      }
    }

    return startMs + this.getMeetingDurationMinutes(meeting) * 60 * 1000;
  }

  private formatLiveEndLabel(meeting: any): string {
    const endMs = this.meetingEndMs(meeting);
    if (!endMs) {
      return '';
    }
    return moment(endMs).format('h:mm A');
  }

  private loadMeetings() {
    this.isLoading = true;
    this.isEmpty = false;
    this.nextMeeting = null;
    this.meetingListCache = [];

    const userInfo = JSON.parse(localStorage.getItem('user'));
    const uniqueUserId = userInfo.UniqueUserId;

    this.dashboardMyTaskService
      .getUserIdbyUniqueUserId(uniqueUserId, true)
      .then((responseForCompanyIdandUserId) => {
        if (responseForCompanyIdandUserId.status) {
          this.finishLoadingEmpty();
          return;
        }

        const companyIdBatch: string[] = _.uniq(
          responseForCompanyIdandUserId.listOfId.map((row: any) =>
            String(row.CompanyId)
          )
        );

        const meetings$ =
          companyIdBatch.length > 0
            ? this.meetingMasterService
                .getMeetingListBatch(companyIdBatch, true, 12, 36)
                .pipe(
                  catchError(() =>
                    forkJoin(
                      companyIdBatch.map((companyId) =>
                        this.meetingMasterService.getMeetingList(
                          companyId,
                          true
                        )
                      )
                    ).pipe(
                      map((responses: any[]) => ({
                        status: 0,
                        MeetingList: _.flattenDeep(
                          responses.map((r) => r?.MeetingList || [])
                        ),
                      }))
                    )
                  )
                )
            : of({ status: 0, MeetingList: [] });

        const regular$ = from(this.companyMasterService.getGEMUrl()).pipe(
          mergeMap((responseCompany: any) => {
            if (
              responseCompany?.status === 0 &&
              _.size(responseCompany.companyData)
            ) {
              const gemURL =
                responseCompany.companyData[0].GEMAccessURL || '';
              if (gemURL) {
                this.gemAccessURL = gemURL;
                return from(
                  this.meetingMasterService.getRegularMeetingList(gemURL, true)
                );
              }
            }
            return of(null);
          }),
          catchError(() => of(null))
        );

        forkJoin({ meetings: meetings$, regular: regular$ }).subscribe(
          (bundle) => {
            this.applyMeetingBundle(bundle, responseForCompanyIdandUserId);
          },
          () => this.finishLoadingEmpty()
        );
      })
      .catch(() => this.finishLoadingEmpty());
  }

  private applyMeetingBundle(
    bundle: { meetings: any; regular: any },
    responseForCompanyIdandUserId: any
  ) {
    const batchResp = bundle.meetings;
    if (batchResp?.status === 1 && !_.size(batchResp?.MeetingList)) {
      this.finishLoadingEmpty();
      return;
    }

    let meetingList: any[] = _.size(batchResp?.MeetingList)
      ? batchResp.MeetingList
      : [];

    if (bundle.regular?.regularMeetingList?.length) {
      meetingList = meetingList.concat(bundle.regular.regularMeetingList);
    }

    if (!meetingList.length) {
      this.finishLoadingEmpty();
      return;
    }

    const userSpecificMeetingList = _.filter(meetingList, (meetingObject) =>
      this.meetingVisibleForUser(
        meetingObject,
        responseForCompanyIdandUserId
      )
    );

    this.meetingListCache = _.sortBy(userSpecificMeetingList, (data: any) =>
      this.meetingStartMs(data)
    );
    this.calendarPrefetchedMeetings = this.meetingListCache.slice();
    this.calendarPrefetchedUserList =
      responseForCompanyIdandUserId.listOfId || [];
    this.refreshGemLiveStatusForTodayMeetings().then(() => {
      this.assignMeetingStatusAndBuildView();
    });
  }

  private assignMeetingStatusAndBuildView(rebuildSummary = true) {
    const nowMs = Date.now();
    const today = moment().startOf('day');

    _.forEach(this.meetingListCache, (o) => {
      const meetingDay = moment(this.getMeetingDateRaw(o)).startOf('day');
      if (!meetingDay.isValid()) {
        _.assign(o, { meetingStatus: 'Past' });
        return;
      }
      if (meetingDay.isSame(today, 'day')) {
        _.assign(o, { meetingStatus: 'Today' });
      } else if (meetingDay.isAfter(today, 'day')) {
        _.assign(o, { meetingStatus: 'Future' });
      } else {
        _.assign(o, { meetingStatus: 'Past' });
      }
    });

    const publishedMeetings = this.meetingListCache.filter((m) =>
      this.isPublishedMeeting(m)
    );

    const liveMeetings = publishedMeetings
      .filter((m) => this.classifyMeetingBucket(m, nowMs) === 'LIVE')
      .sort((a, b) => this.meetingStartMs(a) - this.meetingStartMs(b));

    const upcomingMeetings = publishedMeetings
      .filter((m) => this.classifyMeetingBucket(m, nowMs) === 'UPCOMING')
      .sort((a, b) => this.meetingStartMs(a) - this.meetingStartMs(b));

    this.earlierTodayMeetings = publishedMeetings
      .filter((m) => this.classifyMeetingBucket(m, nowMs) === 'EARLIER_TODAY')
      .sort((a, b) => this.meetingStartMs(b) - this.meetingStartMs(a));

    this.nextMeetingIsLive = liveMeetings.length > 0;
    this.nextMeeting = this.nextMeetingIsLive
      ? liveMeetings[0]
      : upcomingMeetings.length
        ? upcomingMeetings[0]
        : null;

    this.nextMeetingLiveEndLabel = this.nextMeetingIsLive
      ? this.formatLiveEndLabel(this.nextMeeting)
      : '';

    if (this.nextMeetingIsLive) {
      this.upcomingMeetings = upcomingMeetings.slice(0, 4);
    } else if (this.nextMeeting) {
      this.upcomingMeetings = upcomingMeetings.slice(1, 5);
    } else {
      this.upcomingMeetings = [];
    }

    if (this.nextMeeting) {
      if (rebuildSummary) {
        this.buildNextMeetingSummary();
      }
      this.isLoading = false;
      this.isEmpty = false;
      if (rebuildSummary && this.meetingNeedsDetailFetch(this.nextMeeting)) {
        this.enrichNextMeeting(this.nextMeeting).then(() => {
          this.buildNextMeetingSummary();
        });
      }
    } else {
      this.nextMeetingIsLive = false;
      this.nextMeetingLiveEndLabel = '';
      this.agendaPreviewItems = [];
      this.upcomingMeetings = [];
      this.isLoading = false;
      this.isEmpty = true;
      if (rebuildSummary) {
        this.calendarPrefetchedMeetings = [];
        this.calendarPrefetchedUserList = [];
      }
    }
  }

  private enrichNextMeeting(meeting: any): Promise<void> {
    const tasks: Promise<any>[] = [];

    if (!meeting.companyName) {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.CompanyName) {
          meeting.companyName = user.CompanyName;
          this.nextMeetingCompanyName = user.CompanyName;
        }
      } catch (e) {}
    }

    if (!meeting.companyName && meeting.CompanyID) {
      tasks.push(
        this.companyMasterService
          .getSingleCompnyDetails(meeting.CompanyID)
          .then((response) => {
            if (response && response.companyData) {
              meeting.companyName = response.companyData.CompanyName;
              this.nextMeetingCompanyName = response.companyData.CompanyName;
            }
          })
          .catch(() => {})
      );
    }

    const meetingId = meeting._id || meeting.meetingId;
    if (meetingId && this.meetingNeedsDetailFetch(meeting)) {
      tasks.push(
        this.meetingMasterService
          .getMeetingById(meetingId, true)
          .then((response) => {
            if (_.size(response.meetingData)) {
              _.assign(meeting, response.meetingData[0]);
            }
          })
          .catch(() => {})
      );
    }

    return Promise.all(tasks).then(() => undefined);
  }

  /** List API already returns most fields; avoid heavy getMeeting when not needed. */
  private meetingNeedsDetailFetch(meeting: any): boolean {
    const hasAgenda = _.size(meeting?.Agenda);
    const hasDocs = !!(meeting?.Documents || meeting?.attachments);
    const hasAttendance = meeting?.Attendance !== undefined;
    return !hasAgenda || !hasDocs || !hasAttendance;
  }

  private buildNextMeetingSummary() {
    const today = moment().startOf('day');
    const meetingDay = moment(this.getMeetingDateRaw(this.nextMeeting)).startOf('day');
    this.daysUntilNextMeeting = meetingDay.isValid()
      ? Math.max(0, meetingDay.diff(today, 'days'))
      : 0;

    this.nextMeetingCompanyName =
      this.nextMeeting.companyName ||
      this.meetingTitle(this.nextMeeting) ||
      'Meeting';

    const agendas = this.getPublishedAgendas(this.nextMeeting);
    this.nextMeetingAgendaCount = agendas.length;
    this.agendaPreviewItems = agendas.slice(0, 5);
    this.nextMeetingDocumentCount = this.countDocuments(this.nextMeeting);
    this.nextMeetingAttendeeCount = this.countAttendees(this.nextMeeting);
    this.nextMeetingDurationLabel = this.estimateDurationLabel(agendas);
    this.buildCalendarUrls(this.nextMeeting);
    this.buildWidgetsFromMeeting(this.nextMeeting);
  }

  private buildWidgetsFromMeeting(meeting: any) {
    this.buildDocumentsWidget(meeting);
    this.buildParticipantsWidget(meeting);
  }

  private buildDocumentsWidget(meeting: any) {
    const docs = meeting && (meeting.Documents || meeting.attachments);
    const items: DirectorDocumentWidgetItem[] = [];
    if (!docs) {
      this.documentsWidgetItems = [];
      return;
    }

    const pushFile = (
      label: string,
      file: any,
      hint: 'notice' | 'agenda' | 'draft' | 'final' | 'atr'
    ) => {
      if (!file || !file.isAvaible) {
        return;
      }
      const fileId = this.resolveDocumentFileId(file, docs, meeting, hint);
      if (!fileId) {
        return;
      }
      const name = file.name || label;
      items.push({
        label,
        name,
        fileId,
        fileExt: this.fileExtFromName(name),
      });
    };

    pushFile('Notice', docs.noticeFile, 'notice');
    pushFile('Draft Minutes', docs.draftFile, 'draft');
    pushFile('Final Minutes', docs.finalFile, 'final');
    pushFile('ATR', docs.ATRFile, 'atr');

    if (_.size(docs.agendaFileArray)) {
      docs.agendaFileArray.forEach((agendaFile: any) => {
        const fileId = this.resolveDocumentFileId(agendaFile, docs, meeting, 'agenda');
        if (!fileId) {
          return;
        }
        const name = agendaFile.name || 'Agenda document';
        items.push({
          label: 'Agenda document',
          name,
          fileId,
          fileExt: this.fileExtFromName(name),
          uploadDate: agendaFile.UploadDate,
        });
      });
    }

    this.documentsWidgetItems = items;
    this.nextMeetingDocumentCount = items.length;
  }

  private buildParticipantsWidget(meeting: any) {
    const attendance = meeting && meeting.Attendance ? meeting.Attendance : [];
    const attending: DirectorParticipantWidgetItem[] = [];
    const notAttending: DirectorParticipantWidgetItem[] = [];

    attendance.forEach((record: any) => {
      const entry: DirectorParticipantWidgetItem = {
        name:
          record.membeberName ||
          record.memberName ||
          record.MemberName ||
          'Participant',
        role: record.memberType || record.MemberType || 'Participant',
      };
      if (record.isAttended) {
        attending.push(entry);
      } else {
        notAttending.push(entry);
      }
    });

    this.participantsHasAttendance = attendance.length > 0;
    this.participantsAttending = attending;
    this.participantsNotAttending = notAttending;
    this.participantsAttendedCount = attending.length;
    this.participantsNotAttendedCount = notAttending.length;
    this.participantsInvited = this.participantsHasAttendance
      ? []
      : this.buildInvitedParticipants(meeting);
    this.participantsWidgetTotal = this.participantsHasAttendance
      ? attendance.length
      : this.participantsInvited.length;
    this.nextMeetingAttendeeCount = this.participantsWidgetTotal || this.countAttendees(meeting);
  }

  private buildInvitedParticipants(meeting: any): DirectorParticipantWidgetItem[] {
    const invited: DirectorParticipantWidgetItem[] = [];
    const push = (list: any[], role: string) => {
      if (!_.size(list)) {
        return;
      }
      list.forEach((person) => {
        invited.push({
          name: this.participantDisplayName(person),
          role,
        });
      });
    };
    push(meeting.Directors, 'Director');
    push(meeting.Members, 'Member');
    push(meeting.Invitees, 'Invitee');
    return invited;
  }

  private participantDisplayName(person: any): string {
    if (!person) {
      return 'Participant';
    }
    const name =
      person.MemberName ||
      person.DirectorName ||
      person.InviteeName ||
      person.name ||
      person.membeberName;
    if (name) {
      return name;
    }
    const first = person.FirstName || '';
    const last = person.LastName || '';
    const combined = `${first} ${last}`.trim();
    return combined || 'Participant';
  }

  private resolveDocumentFileId(
    file: any,
    docRoot: any,
    meeting: any,
    hint: 'notice' | 'agenda' | 'draft' | 'final' | 'atr' = 'agenda'
  ): string {
    const normalize = (v: any): string => {
      if (v === null || v === undefined) {
        return '';
      }
      const s = String(v).trim();
      if (!s || s === 'undefined' || s === 'null') {
        return '';
      }
      return s;
    };

    if (typeof file === 'string') {
      return normalize(file);
    }
    if (!file || typeof file !== 'object') {
      return '';
    }

    const direct = normalize(
      file.docId ??
        file.docID ??
        file.documentId ??
        file.DocumentID ??
        file.DocumentId ??
        file.documentID ??
        file._id ??
        file.id ??
        file.DocumentManagementID ??
        file.documentManagementID
    );
    if (direct) {
      return direct;
    }

    const atr0 = meeting?.AtrDocument?.[0] || {};
    const fallback =
      hint === 'draft'
        ? normalize(
            docRoot?.draftFile?.docId ??
              docRoot?.draftFile?._id ??
              atr0?.DraftDocumentID ??
              atr0?.DraftMinutesDocumentID ??
              atr0?.MinutesDocumentID
          )
        : hint === 'final'
          ? normalize(
              docRoot?.finalFile?.docId ??
                docRoot?.finalFile?._id ??
                atr0?.FinalDocumentID ??
                atr0?.FinalMinutesDocumentID ??
                atr0?.MinutesDocumentID
            )
          : hint === 'notice'
            ? normalize(
                docRoot?.noticeFile?.docId ??
                  docRoot?.noticeFile?._id ??
                  atr0?.NoticeDocumentID
              )
            : hint === 'atr'
              ? normalize(
                  docRoot?.ATRFile?.docId ??
                    docRoot?.ATRFile?._id ??
                    atr0?.ATRDocumentID
                )
              : normalize(
                  docRoot?.agendaFile?.docId ??
                    docRoot?.agendaFile?._id ??
                    atr0?.AgendaDocumentID
                );
    return fallback;
  }

  private fileExtFromName(name: string): string {
    if (!name || typeof name !== 'string') {
      return '';
    }
    const i = name.lastIndexOf('.');
    return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
  }

  private isPdfExt(ext: string): boolean {
    return (ext || '').toLowerCase() === 'pdf';
  }

  private buildCalendarUrls(meeting: any) {
    if (!meeting || meeting.MeetType === 'Circular Resolution') {
      this.calendarUrls = null;
      return;
    }

    const newEvent: ICalendarEvent = {
      title: '',
      start: new Date(),
      duration: 240,
      end: new Date(),
      address: '',
      description: '',
    };

    if (meeting.meetingType != undefined) {
      newEvent.title = meeting.meetingType;
      const start = meeting.meetingDate
        ? moment(new Date(meeting.meetingDate)).format('YYYY-MM-DD')
        : ' ';
      const time = meeting.meetingTime ? meeting.meetingTime : ' ';
      newEvent.start = new Date(start + ' ' + time);
      newEvent.end = new Date(newEvent.start.getTime() + 14400000);
      newEvent.address = meeting.meetingPlace ? meeting.meetingPlace : ' ';
      const options: any = { year: 'numeric', month: 'long', day: 'numeric' };
      newEvent.description =
        'Meeting Type : ' +
        newEvent.title +
        '.' +
        '\\n\\n' +
        'Meeting Date : ' +
        new Date(meeting.meetingDate).toLocaleDateString('en-GB', options) +
        '\\n' +
        'Meeting Time :' +
        time;
    } else {
      newEvent.title = meeting.MeetingTypeID
        ? meeting.MeetingTypeID.MeetingType
          ? meeting.MeetingTypeID.MeetingType
          : ' '
        : ' ';
      const start = meeting.MeetingDate
        ? moment(new Date(meeting.MeetingDate)).format('YYYY-MM-DD')
        : ' ';
      const time = meeting.MeetingTime ? meeting.MeetingTime : ' ';
      newEvent.start = new Date(start + ' ' + time);
      newEvent.end = new Date(newEvent.start.getTime() + 14400000);
      const options: any = { year: 'numeric', month: 'long', day: 'numeric' };
      const issueNoticeDate = new Date(
        meeting.IssueNoticeDueDate ? meeting.IssueNoticeDueDate : ' '
      ).toLocaleDateString('en-GB', options);
      newEvent.address = meeting.MeetingPlace ? meeting.MeetingPlace : ' ';
      newEvent.description =
        'Meeting Type : ' +
        newEvent.title +
        '.' +
        '\\n\\n' +
        'Meeting Date : ' +
        new Date(meeting.MeetingDate).toLocaleDateString('en-GB', options) +
        '\\n' +
        'Meeting Time :' +
        time +
        '\\n\\n' +
        'Due date to issue notice : ' +
        issueNoticeDate;
    }

    const yahooString = this.addToCalendarService.getHrefFor(
      this.addToCalendarService.calendarType.yahoo,
      newEvent
    );
    const yahooParts = yahooString.split('?v');

    this.calendarUrls = {
      google: this.sanitizer.bypassSecurityTrustUrl(
        this.addToCalendarService.getHrefFor(
          this.addToCalendarService.calendarType.google,
          newEvent
        )
      ),
      iCalendar: this.sanitizer.bypassSecurityTrustUrl(
        this.addToCalendarService.getHrefFor(
          this.addToCalendarService.calendarType.iCalendar,
          newEvent
        )
      ),
      outlook: this.sanitizer.bypassSecurityTrustUrl(
        this.addToCalendarService.getHrefFor(
          this.addToCalendarService.calendarType.outlook,
          newEvent
        )
      ),
      yahoo: this.sanitizer.bypassSecurityTrustUrl(
        'https://calendar.yahoo.com/?v' + yahooParts[1]
      ),
      microsoft: this.sanitizer.bypassSecurityTrustUrl(
        this.addToCalendarService.getHrefFor(
          this.addToCalendarService.calendarType.outlookLive,
          newEvent
        )
      ),
    };
  }

  private getPublishedAgendas(meeting: any): any[] {
    const agendas = meeting && meeting.Agenda ? meeting.Agenda : [];
    return _.sortBy(
      agendas.filter((a) => a.isPublished === true),
      (o) => o.Agendaorder
    );
  }

  private isPublishedMeeting(meeting: any): boolean {
    if (meeting.meetingType === 'REGULAR_MEETING') {
      return true;
    }
    return meeting.IsPublished === true;
  }

  private countDocuments(meeting: any): number {
    const docs = meeting.Documents || meeting.attachments;
    if (!docs) {
      return 0;
    }
    let count = 0;
    ['noticeFile', 'agendaFile', 'draftFile', 'finalFile', 'ATRFile'].forEach((key) => {
      if (docs[key] && docs[key].isAvaible) {
        count += 1;
      }
    });
    if (_.size(docs.agendaFileArray)) {
      count += docs.agendaFileArray.length;
    }
    return count;
  }

  private countAttendees(meeting: any): number {
    const directors = _.size(meeting.Directors);
    const members = _.size(meeting.Members);
    const invitees = _.size(meeting.Invitees);
    return directors + members + invitees;
  }

  private estimateDurationLabel(agendas: any[]): string {
    if (!agendas.length) {
      return '90 min';
    }
    return `${Math.max(agendas.length * 10, 30)} min`;
  }

  private finishLoadingEmpty() {
    this.isLoading = false;
    this.isEmpty = true;
    this.nextMeeting = null;
    this.nextMeetingIsLive = false;
    this.nextMeetingLiveEndLabel = '';
    this.upcomingMeetings = [];
    this.earlierTodayMeetings = [];
    this.agendaPreviewItems = [];
    this.calendarUrls = null;
    this.documentsWidgetItems = [];
    this.participantsAttending = [];
    this.participantsNotAttending = [];
    this.participantsInvited = [];
    this.participantsHasAttendance = false;
    this.participantsWidgetTotal = 0;
    this.calendarPrefetchedMeetings = [];
    this.calendarPrefetchedUserList = [];
  }

  meetingTypeLabel(m: any): string {
    if (m.MeetType == 'Meeting') {
      return m.MeetingTypeName || 'Board meeting';
    }
    return m.MeetingTypeName ? 'CR-' + m.MeetingTypeName : 'Meeting';
  }

  meetingTitle(m: any): string {
    if (m.meetingType) {
      return m.meetingName || m.companyName || '';
    }
    return m.MeetingTypeID && m.MeetingTypeID.MeetingType
      ? m.MeetingTypeID.MeetingType
      : m.companyName || '';
  }

  meetingVenue(m: any): string {
    return m.MeetingPlace || m.meetingPlace || 'Venue to be confirmed';
  }

  meetingMonthShort(m: any): string {
    const d = this.getMeetingDateRaw(m);
    if (!d) {
      return '';
    }
    return moment(d).format('MMM').toUpperCase();
  }

  meetingDayNum(m: any): string {
    const d = this.getMeetingDateRaw(m);
    if (!d) {
      return '';
    }
    return moment(d).format('DD');
  }

  meetingWeekdayShort(m: any): string {
    const d = this.getMeetingDateRaw(m);
    if (!d) {
      return '';
    }
    return moment(d).format('ddd');
  }

  meetingTimeDisplay(m: any): string {
    return m.meetingType ? m.meetingTime : m.MeetingTime;
  }

  upcomingMeta(m: any): string {
    const day = this.meetingWeekdayShort(m);
    const time = this.meetingTimeDisplay(m);
    const agendaCount = this.getPublishedAgendas(m).length;
    return `${day} · ${time} · ${agendaCount} agenda items`;
  }

  prepStatusLabel(m: any): string {
    const agendas = this.getPublishedAgendas(m);
    if (!agendas.length) {
      return 'Draft';
    }
    const unpublished = (m.Agenda || []).some((a) => a.isPublished === false);
    return unpublished ? 'Prep needed' : 'Ready';
  }

  formatAgendaIndex(agenda: any, index: number): string {
    const raw = agenda && agenda.AgendaNumber != null ? String(agenda.AgendaNumber) : '';
    const digits = raw.replace(/\D/g, '');
    if (digits) {
      return digits.length >= 2 ? digits.slice(-2) : ('0' + digits).slice(-2);
    }
    return ('0' + (index + 1)).slice(-2);
  }

  agendaAttachmentCount(agenda: any): number {
    const generationPdf = agenda && agenda.AgendaGenerationPdf;
    if (Array.isArray(generationPdf)) {
      return generationPdf.length;
    }
    if (_.size(agenda && agenda.fileType)) {
      return agenda.fileType.length;
    }
    if (generationPdf && generationPdf.IsPdfAvailable) {
      return 1;
    }
    return 0;
  }

  hasAgendaDocument(agenda: any): boolean {
    return !!this.getAgendaPdfDocumentId(agenda);
  }

  openAgendaPdf(agenda: any) {
    const fileId = this.getAgendaPdfDocumentId(agenda);
    if (!fileId) {
      return;
    }

    this.pdfModalTitle = (agenda && agenda.AgendaTitle) || 'Agenda document';
    this.pdfModalSubtitle =
      agenda && agenda.AgendaNumber != null ? `Agenda ${agenda.AgendaNumber}` : '';
    this.pdfModalOpen = true;
    this.pdfLoading = true;
    this.pdfError = '';
    this.pdfIframeUrl = null;
    document.body.style.overflow = 'hidden';

    this.meetingMasterService
      .getAttendanceFileName(fileId)
      .then((response) => {
        if (!response || !response.data || !response.data.Filename) {
          this.pdfLoading = false;
          this.pdfError = 'Document could not be loaded.';
          return;
        }
        const url =
          environment.PSSFileurl +
          '/Document_Type/' +
          response.data.Filename +
          '#toolbar=0';
        this.pdfIframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.pdfLoading = false;
      })
      .catch(() => {
        this.pdfLoading = false;
        this.pdfError = 'Unable to load this document. Please try again later.';
      });
  }

  closeAgendaPdfModal() {
    this.pdfModalOpen = false;
    this.pdfLoading = false;
    this.pdfError = '';
    this.pdfIframeUrl = null;
    if (!this.participantsModalOpen) {
      document.body.style.overflow = '';
    }
  }

  openAgendaPdfViewer(agenda: any, fileEvent?: any) {
    if (!this.nextMeeting) {
      return;
    }
    const modalRef = this.modalService.open(MeetingdetailsComponent, {
      windowClass: 'modal-xlC',
    });
    modalRef.componentInstance.meetingObject = this.nextMeeting;
    modalRef.componentInstance.agendaPdfOnlyMode = true;

    if (fileEvent && fileEvent.FinalUrl) {
      modalRef.componentInstance.autoOpenCurrentAgenda = fileEvent;
    } else if (agenda) {
      const fileId = this.getAgendaPdfDocumentId(agenda);
      if (fileId) {
        modalRef.componentInstance.autoOpenFileId = fileId;
        const page =
          agenda.AgendaNumber != null
            ? parseInt(String(agenda.AgendaNumber), 10)
            : 1;
        modalRef.componentInstance.autoOpenAgendaPage = page || 1;
      }
    }
    this.bindMeetingModalResult(modalRef);
  }

  private getAgendaPdfDocumentId(agenda: any): string | null {
    if (!agenda) {
      return null;
    }
    if (
      agenda.AgendaConvertedDocxToPdf &&
      agenda.AgendaConvertedDocxToPdf.DocumentManagementID
    ) {
      return agenda.AgendaConvertedDocxToPdf.DocumentManagementID;
    }
    const generationPdf = agenda.AgendaGenerationPdf;
    if (Array.isArray(generationPdf) && generationPdf.length) {
      const withId = generationPdf.find((item) => item && item.DocumentManagementID);
      return withId ? withId.DocumentManagementID : null;
    }
    if (generationPdf && generationPdf.DocumentManagementID) {
      return generationPdf.DocumentManagementID;
    }
    return null;
  }

  private getMeetingDateRaw(m: any): any {
    if (m.meetingType == 'REGULAR_MEETING') {
      return m.meetingDate || m.MeetingDate;
    }
    return m.MeetingDate || m.meetingDate;
  }

  private meetingStartMs(meeting: any): number {
    try {
      const datePart = this.getMeetingDateRaw(meeting);
      const timePart = meeting.meetingType ? meeting.meetingTime : meeting.MeetingTime;
      if (!datePart) {
        return 0;
      }
      const dateStr = moment(datePart).format('YYYY-MM-DD');
      if (!moment(dateStr, 'YYYY-MM-DD', true).isValid()) {
        return 0;
      }
      if (timePart) {
        const parsed = moment(`${dateStr} ${timePart}`, [
          'YYYY-MM-DD h:mm A',
          'YYYY-MM-DD hh:mm A',
          'YYYY-MM-DD H:mm',
          'YYYY-MM-DD HH:mm',
        ]);
        if (parsed.isValid()) {
          return parsed.valueOf();
        }
      }
      return moment(dateStr, 'YYYY-MM-DD').startOf('day').valueOf();
    } catch (e) {
      return 0;
    }
  }

  private meetingVisibleForUser(
    meetingObject: any,
    responseForCompanyIdandUserId: any
  ): boolean {
    if (_.size(_.get(meetingObject, 'Directors'))) {
      const directorArray = _.find(_.get(meetingObject, 'Directors'), (directorObject) => {
        if (_.get(directorObject, 'UserGovevaId') != undefined) {
          const assignedUser = _.find(
            responseForCompanyIdandUserId.listOfId,
            (element) => element._id == _.get(directorObject, 'UserGovevaId')
          );
          if (!_.isUndefined(assignedUser)) {
            return directorObject;
          }
        }
      });
      if (_.size(directorArray)) {
        return true;
      }
    }
    if (_.size(_.get(meetingObject, 'Members'))) {
      const memberArray = _.find(_.get(meetingObject, 'Members'), (memberObject) => {
        const assignedUser = _.find(
          responseForCompanyIdandUserId.listOfId,
          (element) => element._id == _.get(memberObject, 'UserGovevaId')
        );
        if (!_.isUndefined(assignedUser)) {
          return memberObject;
        }
      });
      if (_.size(memberArray)) {
        return true;
      }
    }
    if (_.size(_.get(meetingObject, 'Invitees'))) {
      const inviteesArray = _.find(_.get(meetingObject, 'Invitees'), (inviteesObject) => {
        const assignedUser = _.find(
          responseForCompanyIdandUserId.listOfId,
          (element) => element._id == _.get(inviteesObject, 'UserGovevaId')
        );
        if (!_.isUndefined(assignedUser)) {
          return inviteesObject;
        }
      });
      if (_.size(inviteesArray)) {
        return true;
      }
    }
    if (_.size(_.get(meetingObject, 'meetingType'))) {
      const meetingType = _.get(meetingObject, 'meetingType');
      if (!_.isUndefined(meetingType) && meetingType == 'REGULAR_MEETING') {
        return true;
      }
    }
    return false;
  }
}
